/**
 * Der einklappbare Abschnitt des Drawers - Portierung von `sec(key, title, inner)`
 * (`index.html:8369-8376`) samt `SEC_COLLAPSED` (`index.html:4605`) und `toggleSec`
 * (`index.html:8377-8380`).
 *
 * Original, woertlich:
 *
 *     const SEC_COLLAPSED = { 'main-verlauf': true }; // eingeklappte Abschnitte (pro Sitzung)
 *     function sec(key, title, inner) {
 *       return `
 *       <div class="section ${SEC_COLLAPSED[key] ? 'collapsed' : ''}" data-sec="${key}">
 *         <h3 onclick="toggleSec('${key}')"><span class="sec-arrow collapse-pair">…</span>${title}</h3>
 *         <div class="sec-body">${inner}</div>
 *       </div>`;
 *     }
 *     function toggleSec(key) {
 *       SEC_COLLAPSED[key] = !SEC_COLLAPSED[key];
 *       document.querySelector(`[data-sec="${key}"]`)?.classList.toggle('collapsed');
 *     }
 *
 * ================= Zwei Eigenheiten, woertlich uebernommen =================
 *
 * 1. `SEC_COLLAPSED` lebt PRO SITZUNG auf Modulebene, nicht pro Drawer. Ein zugeklappter Abschnitt
 *    bleibt darum ueber `closeDrawer`/`openDrawer` hinweg zu, auch fuer eine ANDERE Bestellung -
 *    der Schluessel enthaelt keine Bestell-id. Genau das bildet das Modul-Global unten nach.
 * 2. Der Anfangsstand ist `{ 'main-verlauf': true }` - der Verlauf startet als einziger zu.
 *
 * ERGAENZT (Reiter „Spedition", 2026-08-20): `'ship-stationen': true` - die Stationsliste des
 * Zeitstrahls startet ebenfalls zugeklappt, aus demselben Grund wie der Stationsverlauf oben: sie
 * ist die laengste Liste des Reiters und selten die erste Frage beim Oeffnen.
 *
 * ERGAENZT (Abschnitt „DSV-Historie", 2026-09-04): `'ship-historie': true` - aus demselben Grund wie
 * `'ship-stationen'` oben: die ungefilterte Ereignisliste kann laenger als der abgeleitete Zeitstrahl
 * werden und ist selten die erste Frage beim Oeffnen.
 *
 * ERGAENZT (Abschnitt „DSV-Belege", Auftrag "DSV-Belege (Zustellbeleg + Rechnung) in der Bestellung
 * anzeigen", 04.09.2026): `'ship-belege': true` - derselbe Grund wie bei den beiden Abschnitten oben,
 * hier zusaetzlich: der Abschnitt ist (anders als `'ship-historie'`) IMMER sichtbar, auch ohne
 * verknuepfte Sendung - zugeklappt faellt er darum in der weit ueberwiegenden Mehrheit der
 * Bestellungen (keine DSV-Belege) nicht als leere Flaeche auf.
 *
 * ERGAENZT (Abschnitt „Entfernte Dokumente“, 2026-10-16): `'docs-entfernt': true` - die Liste ist
 * Nachweis, nicht Arbeitsflaeche, und erscheint ohnehin nur, wenn schon etwas entfernt wurde.
 *
 * ================= Eine bewusste Abweichung in der MECHANIK, nicht im Ergebnis ==================
 *
 * `toggleSec` im Original fasst das DOM direkt an (`classList.toggle`) und zeichnet NICHT neu.
 * Hier haelt die Komponente den Stand zusaetzlich in `useState`, damit React das `class`-Attribut
 * besitzt - ein direkter `classList`-Griff wuerde beim naechsten Neuzeichnen von React
 * ueberschrieben. Das Modul-Global wird beim Umschalten MITGESCHRIEBEN, damit Eigenheit 1 erhalten
 * bleibt. Beobachtbares Ergebnis (Klassenliste am `.section`) ist identisch.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

/**
 * `index.html:4605`, woertlich einschliesslich des Anfangswerts. Modulebene = "pro Sitzung",
 * wie im Bestand.
 */
export const SEC_COLLAPSED: Record<string, boolean> = {
  "main-verlauf": true,
  "ship-stationen": true,
  "ship-historie": true,
  "ship-belege": true,
  "docs-entfernt": true,
};

/** Nur fuer Tests: den Sitzungsstand auf den Anfangswert des Bestands zuruecksetzen. */
export function secCollapsedZuruecksetzen(): void {
  for (const schluessel of Object.keys(SEC_COLLAPSED)) delete SEC_COLLAPSED[schluessel];
  SEC_COLLAPSED["main-verlauf"] = true;
  SEC_COLLAPSED["ship-stationen"] = true;
  SEC_COLLAPSED["ship-historie"] = true;
  SEC_COLLAPSED["ship-belege"] = true;
  SEC_COLLAPSED["docs-entfernt"] = true;
}

/**
 * ERGAENZT (Auftrag "Klick auf nicht aenderbare Grid-Zelle oeffnet die Bestellung an der passenden
 * Stelle"): der gezielte Sprung. `abschnittAnspringen(schluessel)` klappt den Abschnitt auf, scrollt
 * ihn an den Anfang des Drawers und hebt ihn `SPRUNG_HERVORHEBUNG_MS` lang hervor
 * (`.abschnitt-sprung`, `styles/neubau/abschnittSprung.css`). Aufgeklappt wird NUR die Darstellung
 * dieses Abschnitts - `SEC_COLLAPSED` (der Sitzungsstand des Nutzers) bleibt unberuehrt: der Sprung
 * ist ein einmaliger Wunsch, kein Umschalten. Die Abschnitte melden sich dafuer beim Einhaengen an;
 * der Rueckgabewert sagt, ob es einen solchen Abschnitt gerade gibt (`false` = nur Reiter, kein Fehler).
 *
 * Das Aufklappen gilt nur fuer EINEN Sprung: `abschnitteAngleichen()` gleicht alle eingehaengten
 * Abschnitte wieder an `SEC_COLLAPSED` an und laeuft vor jedem Sprung-Lauf des Drawers (auch ohne
 * Ziel-Abschnitt). So bleibt ein aufgesprungener Abschnitt nicht offen, wenn der Drawer auf eine
 * andere Bestellung im selben Reiter wechselt. Manuelles Umschalten schreibt `SEC_COLLAPSED` mit -
 * das Angleichen aendert darum nur Abschnitte, die ein Sprung geoeffnet hat.
 */
export const SPRUNG_HERVORHEBUNG_MS = 1500;

interface SprungHoerer {
  springen: () => void;
  angleichen: () => void;
}

const SPRUNG_HOERER = new Map<string, Set<SprungHoerer>>();

export function abschnittAnspringen(schluessel: string): boolean {
  const hoerer = SPRUNG_HOERER.get(schluessel);
  if (!hoerer || hoerer.size === 0) return false;
  for (const eintrag of Array.from(hoerer)) eintrag.springen();
  return true;
}

/** Gleicht jeden eingehaengten Abschnitt an `SEC_COLLAPSED` an (siehe oben); beruehrt `SEC_COLLAPSED` nicht. */
export function abschnitteAngleichen(): void {
  for (const hoerer of SPRUNG_HOERER.values()) {
    for (const eintrag of Array.from(hoerer)) eintrag.angleichen();
  }
}

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../Ikone.tsx";

export interface AbschnittProps {
  /** `key` im Original - Schluessel in `SEC_COLLAPSED` und Wert von `data-sec`. */
  schluessel: string;
  titel: ReactNode;
  children?: ReactNode;
}

export function Abschnitt({ schluessel, titel, children }: AbschnittProps) {
  const [zu, setZu] = useState<boolean>(() => !!SEC_COLLAPSED[schluessel]);
  const [sprung, setSprung] = useState(false);
  // Zaehlt die Spruenge: ein zweiter Sprung, waehrend die Hervorhebung des ersten noch laeuft
  // (`sprung` bleibt true), muss Scrollen und Zeitgeber trotzdem neu anstossen.
  const [sprungNr, setSprungNr] = useState(0);
  const wurzel = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const eintrag: SprungHoerer = {
      springen: () => {
        setZu(false);
        setSprung(true);
        setSprungNr((n) => n + 1);
      },
      angleichen: () => {
        setZu(!!SEC_COLLAPSED[schluessel]);
        setSprung(false);
      },
    };
    const menge = SPRUNG_HOERER.get(schluessel) ?? new Set<SprungHoerer>();
    menge.add(eintrag);
    SPRUNG_HOERER.set(schluessel, menge);
    return () => {
      menge.delete(eintrag);
      if (menge.size === 0 && SPRUNG_HOERER.get(schluessel) === menge) SPRUNG_HOERER.delete(schluessel);
    };
  }, [schluessel]);

  // Erst NACH dem Aufklappen (eigener Render), damit das Ziel seine endgueltige Lage hat.
  useEffect(() => {
    if (!sprung) return;
    wurzel.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    const zeitgeber = setTimeout(() => setSprung(false), SPRUNG_HERVORHEBUNG_MS);
    return () => clearTimeout(zeitgeber);
  }, [sprung, sprungNr]);

  const umschalten = (): void => {
    // Reihenfolge wie im Original: erst das Sitzungs-Global, dann die Darstellung. Umgeschaltet wird
    // vom DARGESTELLTEN Stand aus: ein per Sprung aufgeklappter Abschnitt (Global noch "zu") klappt
    // beim ersten Klick auf die Ueberschrift zu, statt scheinbar nichts zu tun.
    const neu = !zu;
    SEC_COLLAPSED[schluessel] = neu;
    setZu(neu);
  };

  return (
    <div
      ref={wurzel}
      className={`section ${zu ? "collapsed" : ""}${sprung ? " abschnitt-sprung" : ""}`}
      data-sec={schluessel}
    >
      <h3 onClick={umschalten}>
        <span className="sec-arrow collapse-pair">
          <Ikone name="chevrons-right" className="collapse-closed" />
          <Ikone name="chevrons-down" className="collapse-open" />
        </span>
        {titel}
      </h3>
      <div className="sec-body">{children}</div>
    </div>
  );
}
